import React from "react";
import styled from "styled-components";
import { useLocation } from "react-router";
import { config } from "../config";
import { useAuthStore } from "../auth/store";
import {
  Card,
  PrimaryButton,
  SecondaryButton,
  StatusPill,
  HelperText,
  List,
  ListItem,
  Muted,
} from "./ui";

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.75rem;
`;

const CardTitle = styled.h3`
  margin: 0 0 0.75rem;
  font-size: 1.175rem;
  font-weight: 600;
  color: #0f172a;
`;

const Highlight = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.9rem;
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.12);
  color: #1d4ed8;
  font-weight: 600;
  font-size: 0.85rem;
  letter-spacing: 0.02em;
`;

const Warning = styled.div`
  margin-top: 1rem;
  border-radius: 0.85rem;
  border: 1px solid rgba(239, 68, 68, 0.35);
  background: rgba(254, 226, 226, 0.75);
  color: #7f1d1d;
  padding: 0.75rem 0.9rem;
  font-size: 0.92rem;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1.5rem;
`;

const formatIsoDateTime = (value: string) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString();
};

const ConnectPage: React.FC = () => {
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const authError = query.get("authError");
  const garminError = query.get("garminError");
  const loggedOut = query.get("loggedOut") === "true";

  const startSignIn = (path: "login" | "register") => {
    window.location.href = `${config.REACT_APP_API_BASE_URL}/auth/${path}`;
  };

  const handleConnect = () => {
    window.location.href = `${config.REACT_APP_API_BASE_URL}/auth`;
  };

  const {
    state: { isAuthenticated, isConnected, error, user, account },
  } = useAuthStore();

  const queryMessage = authError
    ? {
        "invalid-state": "Your sign-in session expired. Please try again.",
        cancelled: "Sign-in was cancelled.",
        "login-failed": "We could not complete sign-in. Please try again.",
      }[authError] || "We could not complete sign-in. Please try again."
    : garminError === "already-linked"
      ? "That Garmin account is already linked to another LaceAI account."
      : garminError
        ? "We could not link Garmin. Please try again."
        : loggedOut
          ? "You have been signed out."
          : null;

  const warning = queryMessage || (error && error !== "Not connected" ? error : null);

  if (!isAuthenticated) {
    return (
      <Grid>
        <Card>
          <Highlight>Step 1</Highlight>
          <CardTitle>Create your LaceAI account</CardTitle>
          <Muted>
            Create your account, then link one Garmin account to your LaceAI profile.
          </Muted>
          <Actions>
            <PrimaryButton type="button" onClick={() => startSignIn("register")}>
              Create account
            </PrimaryButton>
            <SecondaryButton type="button" onClick={() => startSignIn("login")}>
              Log in
            </SecondaryButton>
          </Actions>
          {warning ? <Warning>{warning}</Warning> : null}
        </Card>

        <Card>
          <Highlight>What to expect</Highlight>
          <List>
            <ListItem>Use your LaceAI account to sign in securely</ListItem>
            <ListItem>Link Garmin after you return to LaceAI</ListItem>
            <ListItem>Your Garmin account is used only for workout sync</ListItem>
          </List>
        </Card>
      </Grid>
    );
  }

  if (isConnected) {
    return (
      <Grid>
        <Card>
          <StatusPill>Connected</StatusPill>
          <CardTitle>Garmin account linked</CardTitle>
          <Muted>
            You can jump straight into generating workouts or browse the library of synced sessions.
          </Muted>
          {user?.email ? <Muted style={{ marginTop: "0.75rem" }}>{user.email}</Muted> : null}
          {account ? (
            <List style={{ marginTop: "1rem" }}>
              <ListItem>
                Garmin User ID: <strong>{account.garminUserId}</strong>
              </ListItem>
              <ListItem>Linked: {formatIsoDateTime(account.linkedAt)}</ListItem>
              <ListItem>Last refreshed: {formatIsoDateTime(account.lastUpdatedAt)}</ListItem>
            </List>
          ) : null}
          <Actions>
            <SecondaryButton
              type="button"
              onClick={() => {
                window.location.href = `${config.REACT_APP_API_BASE_URL}/auth/logout`;
              }}
            >
              Log out
            </SecondaryButton>
          </Actions>
        </Card>
      </Grid>
    );
  }

  return (
    <Grid>
      <Card>
        <Highlight>Step 2</Highlight>
        <CardTitle>Link your Garmin account</CardTitle>
        <Muted>
          We securely redirect you to Garmin to approve access. LaceAI only requests the scopes required to create workouts on your behalf. One Garmin account can be linked to this LaceAI account.
        </Muted>
        <Actions>
          <PrimaryButton type="button" onClick={handleConnect}>
            Connect Garmin
          </PrimaryButton>
          <SecondaryButton
            type="button"
            onClick={() => {
              window.location.href = `${config.REACT_APP_API_BASE_URL}/auth/logout`;
            }}
          >
            Log out
          </SecondaryButton>
        </Actions>
        <HelperText>After granting access you&rsquo;ll land back here automatically.</HelperText>
        {warning ? <Warning>{warning}</Warning> : null}
      </Card>

      <Card>
        <Highlight>What to expect</Highlight>
        <List>
          <ListItem>Read-only access to your basic profile for account linking</ListItem>
          <ListItem>Create and push structured workouts directly to Garmin</ListItem>
          <ListItem>Revoke access any time from your Garmin account dashboard</ListItem>
        </List>
      </Card>
    </Grid>
  );
};

export default ConnectPage;
